import { useState, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  patchInline,
  postInline,
  toastInlineUpdateError,
  toastInlineUpdateSuccess,
  type InlineUpdateResult,
} from "@/lib/inline-update"
import {
  cancellationRequestComplete,
  tCancellation,
  type CancellationReasonOption,
  type CancelPayload,
} from "@/features/staff/cancellations"

/**
 * Why something was cancelled, asked once and shared by every surface that
 * cancels: a bill from the table workspace, an order from its own detail
 * drawer. `Location::CancellationPolicy.label` raises on a reason it does not
 * recognise, so this dialog is the one place in the browser allowed to
 * produce one — it only ever offers the catalog the server sent, and never a
 * free-text field.
 *
 * A dialog rather than a confirm-on-click, for the reason `DiscountAction`
 * already states for a discount: the value it collects is chosen, not typed,
 * and a cancellation nobody can name is indistinguishable from a walkout,
 * from a kitchen failure and from theft — which is the report
 * `Location::CancellationPolicy` exists to make possible.
 *
 * ## It also serves reasons that are not cancellations
 *
 * A stock adjustment asks the identical question — *why did this happen, from a
 * list nobody may add to* — and answering it with a second dialog would mean two
 * places deciding when the confirm button is allowed, when Escape may close
 * mid-request, and how a reason reaches the server. `copy`, `method`,
 * `testIdPrefix`, `children` and `ready` are what let one component do both
 * without either caller inheriting the other's wording. Everything optional
 * defaults to the cancellation behaviour, so a cancellation call site reads
 * exactly as it did.
 */

/**
 * Every string the dialog puts on screen. Supplied whole rather than merged over
 * a default, so a caller that overrides the title cannot end up with a confirm
 * button that still says "Confirmar cancelación".
 */
export type ReasonDialogCopy = {
  title: string
  description: string
  reasonLabel: string
  reasonPlaceholder: string
  cancel: string
  confirm: string
  submitting: string
}

export type CancelReasonDialogProps = {
  /** Where the reason is posted — a bill's `/cancel` or an order's own PATCH. */
  path: string
  /** The reason, shaped into the body the endpoint expects. */
  payload: (reason: string) => CancelPayload
  reasons: CancellationReasonOption[]
  /** Withholds the trigger while another action on the same resource is in flight. */
  disabled?: boolean
  triggerLabel: string
  triggerVariant?: "destructive" | "outline" | "ghost" | "default"
  triggerSize?: "default" | "sm" | "icon-sm"
  triggerClassName?: string
  /**
   * Per-instance, and required rather than defaulted: the table workspace mounts
   * one of these per closed bill for cancelling and another for reopening, so a
   * single shared id would leave four indistinguishable triggers on one screen
   * and no way for a test -- or a screen reader -- to tell which bill it is
   * about.
   */
  triggerTestId: string
  /** Extra work once the server confirms — the drawer closes itself, the table workspace needs nothing more. */
  onSuccess?: (result: InlineUpdateResult) => void
  /**
   * PATCH by default, which is what every cancellation is. A stock adjustment
   * POSTs, because it creates a ledger row rather than changing a record's
   * state — and the ledger has no state to change: its rows are readonly.
   */
  method?: "patch" | "post"
  /** Defaults to the cancellation wording. */
  copy?: ReasonDialogCopy
  /**
   * Destructive by default, which a cancellation is. Recording that stock was
   * counted is not destroying anything, and dressing an ordinary entry in the
   * colour reserved for irreversible loss is how that colour stops meaning
   * anything.
   */
  confirmVariant?: "destructive" | "default"
  /**
   * Namespaces the dialog's own testids so two different reason dialogs on one
   * screen are distinguishable. Defaults to the ids the cancellation E2E suite
   * already targets; do not change that default.
   */
  testIdPrefix?: string
  /** Extra fields rendered under the reason — a quantity, a note. */
  children?: ReactNode
  /**
   * The chosen reason, published as it changes.
   *
   * A cancellation needs nothing from this; a stock adjustment needs everything,
   * because `count_correction` and `waste` ask the operator for two different
   * numbers and the quantity field's own label depends on which was picked.
   */
  onReasonChange?: (reason: string) => void
  /**
   * Whether the caller's own extra fields are filled in. ANDed with "a reason is
   * chosen"; a dialog with no extra fields leaves it at `true`.
   */
  ready?: boolean
  /** Clears the caller's extra fields when the dialog closes or commits. */
  onReset?: () => void
}

export function CancelReasonDialog({
  path,
  payload,
  reasons,
  disabled = false,
  triggerLabel,
  triggerVariant = "destructive",
  triggerSize = "sm",
  triggerClassName,
  triggerTestId,
  onSuccess,
  method = "patch",
  copy,
  confirmVariant = "destructive",
  testIdPrefix = "cancel-reason",
  children,
  onReasonChange,
  ready = true,
  onReset,
}: CancelReasonDialogProps) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const text: ReasonDialogCopy = copy ?? {
    title: tCancellation("title"),
    description: tCancellation("description"),
    reasonLabel: tCancellation("reason_label"),
    reasonPlaceholder: tCancellation("reason_placeholder"),
    cancel: tCancellation("cancel"),
    confirm: tCancellation("confirm"),
    submitting: tCancellation("submitting"),
  }

  const submit = method === "post" ? postInline : patchInline
  const complete = cancellationRequestComplete(reason) && ready

  const chooseReason = (next: string) => {
    setReason(next)
    onReasonChange?.(next)
  }

  const reset = () => {
    chooseReason("")
    onReset?.()
  }

  const handleOpenChange = (next: boolean) => {
    // Escape and the overlay both route through here. A dialog that vanishes
    // mid-request leaves the operator with no idea whether the cancellation
    // went through.
    if (submitting) return

    setOpen(next)
    if (!next) reset()
  }

  const handleConfirm = () => {
    if (submitting || !complete) return

    setSubmitting(true)
    submit(path, payload(reason), { preserveState: true })
      .then((result) => {
        setOpen(false)
        reset()
        toastInlineUpdateSuccess(result)
        onSuccess?.(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <>
      <Button
        type="button"
        variant={triggerVariant}
        size={triggerSize}
        className={triggerClassName}
        disabled={disabled}
        onClick={() => handleOpenChange(true)}
        data-testid={triggerTestId}
      >
        {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{text.title}</DialogTitle>
            <DialogDescription>{text.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`${testIdPrefix}-field`}>{text.reasonLabel}</Label>
              <Select value={reason} onValueChange={chooseReason}>
                <SelectTrigger
                  id={`${testIdPrefix}-field`}
                  className="w-full"
                  data-testid={testIdPrefix}
                >
                  <SelectValue placeholder={text.reasonPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`${testIdPrefix}-option-${option.value}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {children}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => handleOpenChange(false)}
              data-testid={`${testIdPrefix}-cancel`}
            >
              {text.cancel}
            </Button>
            <Button
              type="button"
              variant={confirmVariant}
              disabled={submitting || !complete}
              onClick={handleConfirm}
              data-testid={`${testIdPrefix}-confirm`}
            >
              {submitting ? text.submitting : text.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
